import React from "react";
import useMaturityData from "../hooks/useMaturityData";
import { LEVELS } from "../utils/levelColors";
import "./dashboard.css";

const DONUT_COLORS = {
  complete: "#27ae60",
  partial: "#e07a1a",
  completeTrack: "rgba(39, 174, 96, 0.25)",
  partialTrack: "#f0f1f5",
};

const LevelDonut = ({ fulfilled, total }) => {
  const size = 30;
  const strokeWidth = 9;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const fraction = total > 0 ? fulfilled / total : 0;
  const isComplete = fraction >= 1;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`${fulfilled} of ${total} questions fulfill this level`}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={
          fraction === 0
            ? DONUT_COLORS.completeTrack
            : DONUT_COLORS.partialTrack
        }
        strokeWidth={strokeWidth}
      />
      {fraction > 0 && (
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={isComplete ? DONUT_COLORS.complete : DONUT_COLORS.partial}
          strokeWidth={strokeWidth}
          strokeDasharray={`${circumference * fraction} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      )}
    </svg>
  );
};

const LevelAchievement = () => {
  const { levelProgress, loading, error } = useMaturityData();

  if (loading) return <div className="dashboard-loading">Loading levels…</div>;
  if (error) return <div className="dashboard-loading">{error}</div>;

  if (!levelProgress) {
    return (
      <>
        <div className="dashboard-header">
          <h1 className="dashboard-title">Level Achievement</h1>
        </div>
        <div className="dashboard-next-steps">
          <p className="dashboard-next-steps-text">
            Complete the questionnaire to see your level achievement by dimension.
          </p>
        </div>
      </>
    );
  }

  const dimensions = Object.entries(levelProgress);

  return (
    <>
      <div className="dashboard-header">
        <h1 className="dashboard-title">Level Achievement</h1>
        <p className="dashboard-subtitle">
          How many answered questions of each dimension fulfill each maturity level
        </p>
      </div>

      <div className="dashboard-table-section">
        <h2 className="dashboard-section-title">Level Achievement by Dimension</h2>
        <p className="dashboard-table-note">
          Each card shows how many of the dimension's questions are answered at
          the respective level or higher. A full green circle means every
          question fulfills that level.
        </p>
        <div className="level-grid-wrapper">
          <div className="level-grid">
            {/* Header */}
            <div className="level-grid-row level-grid-row--header">
              <div className="level-grid-cell level-grid-cell--dim">Dimension</div>
              <div className="level-grid-cell">Score</div>
              {LEVELS.map((l) => (
                <div key={l} className="level-grid-cell">
                  Level {l}
                </div>
              ))}
            </div>

            {/* Data rows */}
            {dimensions.map(([dim, data], idx) => {
              const pct =
                data.maxScore > 0
                  ? ((data.score / data.maxScore) * 100).toFixed(2)
                  : "0.00";
              return (
                <div
                  key={dim}
                  className={`level-grid-row${idx % 2 === 0 ? " level-grid-row--even" : ""}`}
                >
                  <div className="level-grid-cell level-grid-cell--dim">
                    {dim}
                  </div>
                  <div className="level-grid-cell">
                    <span className="level-score-pill">{pct}%</span>
                  </div>
                  {LEVELS.map((l) => {
                    const { fulfilled = 0, total = 0 } =
                      data.levelFulfillment?.[l] ?? {};
                    return (
                      <div key={l} className="level-grid-cell">
                        <div
                          className="level-card"
                          title={`${fulfilled} of ${total} questions fulfill Level ${l}`}
                        >
                          <span className="level-card-count">
                            {fulfilled} of {total}
                          </span>
                          <LevelDonut fulfilled={fulfilled} total={total} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default LevelAchievement;
